import { ComponentHealthCondition } from 'features/fde/assetHealthPlanning/componentHealthConditions'
import { AggregateUnitData } from 'features/overview/aggregateSiteData'
import { NoRLHealthScore, TraditionalInspectionTypes } from 'types'

import { FilterFunc } from './types/unitFilteringTypes'

type DataQualityNums = {
  aiData: number
  traditionalData: number
}

export type AssetTrackerBreakdown = {
  [ComponentHealthCondition.Excellent]: DataQualityNums
  [ComponentHealthCondition.Fair]: DataQualityNums
  [ComponentHealthCondition.Bad]: DataQualityNums
  [ComponentHealthCondition.VeryPoor]: DataQualityNums
  [NoRLHealthScore.NoInspection]: number
  [NoRLHealthScore.NotEnoughData]: number
}

/**
 * A function that organizes the data in an easily digestible way for the asset tracker.
 */
export function assembleAssetTrackerBreakdown(
  aggregateData: ReadonlyArray<AggregateUnitData>,
  dataFilters: FilterFunc,
  selectedYear: number
): AssetTrackerBreakdown {
  return aggregateData.reduce<AssetTrackerBreakdown>(
    (aggATB, unitAggData) => {
      // If the unit is filtered out, skip it
      if (!dataFilters(unitAggData)) {
        return aggATB
      }
      const healthCondition =
        unitAggData.health[selectedYear].unitHealthScoreForYear

      if (
        healthCondition === NoRLHealthScore.NoInspection ||
        healthCondition === NoRLHealthScore.NotEnoughData
      ) {
        return {
          ...aggATB,
          [healthCondition]: aggATB[healthCondition] + 1,
        }
      }

      const inspectionType =
        unitAggData.mostRecentInspectionData.inspection?.data.inspection_type

      const inspectionCategory = TraditionalInspectionTypes.includes(
        inspectionType ?? ''
      )
        ? 'traditionalData'
        : 'aiData'

      return {
        ...aggATB,
        [healthCondition]: {
          ...aggATB[healthCondition],
          [inspectionCategory]: aggATB[healthCondition][inspectionCategory] + 1,
        },
      }
    },
    {
      [ComponentHealthCondition.Excellent]: {
        aiData: 0,
        traditionalData: 0,
      },
      [ComponentHealthCondition.Fair]: {
        aiData: 0,
        traditionalData: 0,
      },
      [ComponentHealthCondition.Bad]: {
        aiData: 0,
        traditionalData: 0,
      },
      [ComponentHealthCondition.VeryPoor]: {
        aiData: 0,
        traditionalData: 0,
      },
      [NoRLHealthScore.NoInspection]: 0,
      [NoRLHealthScore.NotEnoughData]: 0,
    }
  )
}
